// Quote calculations, shared by the quote editor, the project summary and the printed quote.
// Prices in a quote are cost prices; the cable / item markups are added on top.

export const VAT_RATE = 0.25;

const num = (v) => (v == null || v === '' || isNaN(v) ? 0 : Number(v));
const sum = (list, fn) => (list || []).reduce((s, x) => s + fn(x), 0);

export const withMarkup = (price, markup) => num(price) * (1 + num(markup) / 100);

export const cablePriceMap = (q) => new Map((q.cablePrices || []).map(p => [p.cableType || '', p.price]));
export const rateMap       = (q) => new Map((q.rates || []).map(r => [r.key, r]));

export function quoteTotals(q) {
  const cablePrice = cablePriceMap(q);
  const rates      = rateMap(q);
  const cableCost  = sum(q.cables, c => num(c.length_m) * num(cablePrice.get(c.cableType || '')));
  const itemCost   = sum(q.itemGroups, g => sum(g.items, i => num(i.qty) * num(i.price)));
  const hours      = sum(q.hours, h => num(h.hours) * num(rates.get(h.rateKey)?.rate));
  const cables     = cableCost * (1 + num(q.cableMarkup) / 100);
  const items      = itemCost * (1 + num(q.itemMarkup) / 100);
  const subtotal   = cables + items + hours;
  const discount   = subtotal * num(q.discount) / 100;
  const net        = subtotal - discount;
  const vat        = q.vat ? net * VAT_RATE : 0;
  return { cableCost, itemCost, cables, items, hours, subtotal, discount, net, vat, total: net + vat };
}

// Cable types used in a quote: [{ cableType, count, length, price }]
export function cableTypeRows(q) {
  const price = cablePriceMap(q);
  const rows = new Map();
  for (const c of q.cables || []) {
    const t = c.cableType || '';
    if (!rows.has(t)) rows.set(t, { cableType: t, count: 0, length: 0, missingLength: 0, price: price.get(t) ?? null });
    const r = rows.get(t);
    r.count += 1;
    r.length += num(c.length_m);
    if (c.length_m == null || c.length_m === '') r.missingLength += 1;
  }
  return [...rows.values()].sort((a, b) => a.cableType.localeCompare(b.cableType, undefined, { numeric: true }));
}

// 1234.5 -> "1 234,50" (plain spaces, so it prints in the PDF font too)
export const money = (n) => {
  const [i, d] = (Math.round(num(n) * 100) / 100).toFixed(2).split('.');
  return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${d}`;
};

export const quoteDate = (d) => (d ? new Date(d).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
